import { SECTIONS, SECTION_STATUS, SECTION_REASON_CODE } from '../constants/patientCard';

/**
 * patientCardLanes (Q2 / QA D3) — THE shared guard every patient-card
 * section routes through.
 *
 * The §1.2 envelope carries three distinct per-section truths: the lane has
 * data (`OK`/`TRUNCATED`), the lane is genuinely empty (`EMPTY`), and the
 * lane COULD NOT BE READ (`UNAVAILABLE`). Reading a section's raw item array
 * (`sections?.<lane>?.items || []`) collapses the third into the second and
 * makes the card assert an absence the server never claimed — on a clinical
 * record. That pattern is banned by review AND by a static source scan
 * (`patientCardLanes.test.js`): this module is the ONLY place a section's
 * `items` array is touched, so a new lane cannot rediscover the defect.
 *
 * Branching is on `reason_code`, NOT on `status` alone:
 *   - `LANE_READ_FAILED` (or any other failure code) — a real read failed,
 *     so a retry is honest → notice + `Try again`.
 *   - `NOT_IMPLEMENTED` — the lane is not built. A retry can NEVER succeed,
 *     so offering one would be a lie → the §12 "not shown here yet" copy,
 *     no retry, no notice.
 *
 * All display strings are PO-owned (`f4f-name-search-copy.md` Part 4 §23/§25)
 * and live here so the notice, the row lines and the print/`—` count rules
 * can never drift apart.
 */

// ── PO copy (Part 4 §23, §25) ────────────────────────────────────────────

/** The ONE denial clause, shared byte-for-byte by the failed-read copy and
 *  the not-built copy (§25.4) — the epic's answer to a false absence reads
 *  the same wherever it appears. */
export const NO_ABSENCE_CLAUSE = "This doesn't mean there are none.";

/** Honest ONLY because Q2 builds the retry control (§23 "Why this wording").
 *  If the control is ever cut, this string must become
 *  "Reload the page to try again." in the same change. */
export const RETRY_INSTRUCTION = 'Try again, or reload the page.';

export const FAILED_NOTICE_BODY = `${NO_ABSENCE_CLAUSE} ${RETRY_INSTRUCTION}`;
export const COLLAPSED_NOTICE_HEADING = "Parts of this record couldn't be loaded";

/** §12 as amended by §25 — the not-built lane. No retry, ever. */
export const NOT_BUILT_HEADING = 'Not shown here yet';
export const NOT_BUILT_BODY = `This part of the patient's record can't be shown on this page yet. ${NO_ABSENCE_CLAUSE}`;

/** A count for a lane we could not read renders THIS, never `0` — a `0`
 *  re-asserts the absence the copy just denied (§24). */
export const COUNT_UNKNOWN = '—';

/** Three or more failed sections collapse to ONE notice, not a stack (§23.1). */
export const NOTICE_COLLAPSE_THRESHOLD = 3;

// Reader-facing noun per lane — the heading, the collapsed list and (for the
// three lanes an OPD row renders) the row line all derive from these, so a
// new section cannot ship with half a vocabulary.
const LANE_NOUN = {
  visits: 'visits',
  prescriptions: 'prescriptions',
  clinical_notes: 'notes',
  procedures: 'procedures',
  labs: 'lab orders',
  admissions: 'admissions',
  documents: 'documents',
  timeline: 'the timeline',
};

// Row-level noun (singular for a prescription — one visit carries one).
const LANE_ROW_NOUN = {
  prescriptions: 'Prescription',
  procedures: 'Procedures',
  labs: 'Lab orders',
};

/** Lanes a section notice can be raised for, in the contract's canonical
 *  order (derived from `SECTIONS`, never a second literal list). `patient`
 *  and `timeline` are excluded: each already owns a page-level state of its
 *  own (`PatientCardHeader` / the timeline region itself). */
export const NOTICE_LANES = SECTIONS.filter((s) => s !== 'patient' && s !== 'timeline');

// ── the guard ────────────────────────────────────────────────────────────

// Only these statuses carry a trustworthy item array. `UNAVAILABLE`,
// `MODULE_DISABLED` and `NOT_REQUESTED` yield NOTHING — a caller therefore
// cannot tell "failed" from "empty" by looking at the array, which is the
// whole point: it has to read the flags.
const READABLE_STATUSES = [SECTION_STATUS.OK, SECTION_STATUS.EMPTY, SECTION_STATUS.TRUNCATED];

/**
 * The single accessor for one SectionObject.
 *
 * @param {object|null|undefined} section — a §1.2 SectionObject.
 * @returns {{status, reasonCode, items: Array, unavailable: boolean,
 *            notBuilt: boolean, canRetry: boolean}}
 */
export function readLane(section) {
  const status = section?.status ?? null;
  const reasonCode = section?.reason_code ?? null;
  const unavailable = status === SECTION_STATUS.UNAVAILABLE;
  const notBuilt = unavailable && reasonCode === SECTION_REASON_CODE.NOT_IMPLEMENTED;
  const readable = READABLE_STATUSES.includes(status) && Array.isArray(section?.items);
  return {
    status,
    reasonCode,
    items: readable ? section.items : [],
    unavailable,
    notBuilt,
    // Retry is offered ONLY where retrying can work.
    canRetry: unavailable && !notBuilt,
  };
}

// ── copy derivation ──────────────────────────────────────────────────────

function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "Prescriptions couldn't be loaded" / "The timeline couldn't be loaded". */
export function laneFailureHeading(laneName) {
  return `${capitalise(LANE_NOUN[laneName] || LANE_NOUN.timeline)} couldn't be loaded`;
}

/** "Prescription for this visit couldn't be loaded." — replaces the ordinary
 *  empty line on EVERY row while that lane is unreadable (§23.2). */
export function laneRowFailureLine(laneName) {
  return `${LANE_ROW_NOUN[laneName]} for this visit couldn't be loaded.`;
}

function joinNouns(laneNames) {
  const nouns = laneNames.map((n) => LANE_NOUN[n]);
  if (nouns.length <= 1) return nouns.join('');
  return `${nouns.slice(0, -1).join(', ')} and ${nouns[nouns.length - 1]}`;
}

/**
 * The notices to render for a `sections` map: one per failed section, or a
 * single collapsed notice once three or more fail (§23.1). A `NOT_IMPLEMENTED`
 * lane NEVER appears here — it renders its own no-retry copy inside the
 * section body instead.
 *
 * @returns {Array<{key: string, heading: string, body: string}>} — empty when
 *   nothing failed.
 */
export function laneFailureNotices(sections, laneNames) {
  const failed = NOTICE_LANES
    .filter((name) => laneNames.includes(name))
    .filter((name) => readLane(sections?.[name]).canRetry);

  if (failed.length === 0) return [];
  if (failed.length >= NOTICE_COLLAPSE_THRESHOLD) {
    return [{
      key: 'collapsed',
      heading: COLLAPSED_NOTICE_HEADING,
      body: `${capitalise(joinNouns(failed))} couldn't be loaded. ${FAILED_NOTICE_BODY}`,
    }];
  }
  return failed.map((name) => ({
    key: name,
    heading: laneFailureHeading(name),
    body: FAILED_NOTICE_BODY,
  }));
}
